import { AdDataFreshnessNote } from '../components/AdDataFreshnessNote';
import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import type { BingDateGrain } from '../api/bingDashboard';
import type { AppLikeOverviewRow } from '../api/applovinDashboard';
import { rtbDashboardApi } from '../api/rtbDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { MultiSelect } from '../components/MultiSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { SortableHeader, type SortValue } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { trackFeatureAction, trackQueryEvent } from '../lib/usageTracking';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';
import { useLazySection } from '../hooks/useLazySection';
import { useDeliverySiteScope } from '../hooks/useDeliverySiteScope';
import AppLikeDeliveryDashboard, { fmtInt, fmtNum, fmtPct } from './AppLikeDeliveryDashboard';

type DatePreset =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | 'week_to_yesterday'
  | 'this_week'
  | 'this_month_today'
  | 'this_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'this_quarter'
  | 'this_year'
  | 'custom';

interface DateRange {
  start: string;
  end: string;
}

export interface MetricField {
  key: string;
  label: string;
  fmt: (value: number) => string;
  permKey?: FieldKey;
}

export interface TrendMetric extends MetricField {
  colorToken: '--primary' | '--green' | '--cyan' | '--text3';
  defaultSelected?: boolean;
}

export interface RtbDimension {
  key: string;
  label: string;
  rowKey: keyof AppLikeOverviewRow;
}

interface RtbMetricAccumulator {
  impressions: number;
  clicks: number;
  addToCartCount: number;
  spend: number;
  purchases: number;
  revenue: number;
}

interface RtbTreeNode {
  id: string;
  depth: number;
  dimKey: string;
  value: string;
  sortValue: string;
  metrics: RtbMetricAccumulator;
  children: RtbTreeNode[];
  childMap: Map<string, RtbTreeNode>;
}

type RtbSortState = { key: string; direction: 'asc' | 'desc' } | null;

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
];

const DATE_DIMENSION: RtbDimension = { key: 'date', label: '日期', rowKey: 'date_label' };
const COUNTRY_DIMENSION: RtbDimension = { key: 'country', label: '国家/地区', rowKey: 'country' };

const DAILY_DATE_PRESETS: Array<{ value: Exclude<DatePreset, 'custom'>; label: string }> = [
  { value: 'today', label: '今天' },
  { value: 'yesterday', label: '昨天' },
  { value: '7d', label: '最近7天' },
  { value: '30d', label: '最近30天' },
  { value: 'week_to_yesterday', label: '本周到昨天' },
  { value: 'this_week', label: '本周' },
  { value: 'this_month_today', label: '本月' },
];

const PERIOD_DATE_PRESETS: Array<{ value: Exclude<DatePreset, 'custom'>; label: string }> = [
  { value: 'this_week', label: '本周' },
  { value: 'this_month', label: '本月' },
  { value: 'last_3_months', label: '最近3个月' },
  { value: 'last_6_months', label: '最近6个月' },
  { value: 'this_quarter', label: '本季度' },
  { value: 'this_year', label: '今年' },
];

const EFFICIENCY_METRICS: MetricField[] = [
  { key: 'spend', label: '账户花费', fmt: (value) => fmtNum(value), permKey: 'spend' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'cpc', label: '账户CPC', fmt: (value) => fmtNum(value), permKey: 'cpc' },
  { key: 'cpa', label: '账户CPA', fmt: (value) => fmtNum(value), permKey: 'cpa' },
  { key: 'aov', label: '账户单均价', fmt: (value) => fmtNum(value), permKey: 'revenue' },
  { key: 'account_roi', label: '账户ROI', fmt: (value) => fmtNum(value), permKey: 'roas' },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'purchases', label: '购物次数', fmt: fmtInt },
  { key: 'revenue', label: '购物转化价值', fmt: (value) => fmtNum(value), permKey: 'revenue' },
];

const TREND_METRICS: TrendMetric[] = [
  { key: 'spend', label: '账户花费', fmt: (value) => fmtNum(value), permKey: 'spend', colorToken: '--primary', defaultSelected: true },
  { key: 'cpc', label: '账户CPC', fmt: (value) => fmtNum(value), permKey: 'cpc', colorToken: '--text3' },
  { key: 'cpa', label: '账户CPA', fmt: (value) => fmtNum(value), permKey: 'cpa', colorToken: '--green', defaultSelected: true },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct, colorToken: '--cyan' },
];

const ZEELOOL_RTB_METRICS = [
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'spend', label: '账户花费', fmt: (value: number) => fmtNum(value), permKey: 'spend' },
  { key: 'purchases', label: '购物次数', fmt: fmtInt },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'cpa', label: '账户CPA', fmt: (value: number) => fmtNum(value), permKey: 'cpa' },
  { key: 'revenue', label: '购物转化价值', fmt: (value: number) => fmtNum(value), permKey: 'revenue' },
  { key: 'account_roi', label: '账户ROI', fmt: (value: number) => fmtNum(value), permKey: 'roas' },
  { key: 'cpc', label: '账户CPC', fmt: (value: number) => fmtNum(value), permKey: 'cpc' },
  { key: 'aov', label: '账户单均价', fmt: (value: number) => fmtNum(value), permKey: 'revenue' },
] as const;

function daysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function dateAtUTC(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

function datePresetsForGrain(grain: BingDateGrain) {
  return grain === 'day' ? DAILY_DATE_PRESETS : PERIOD_DATE_PRESETS;
}

function defaultDatePresetForGrain(grain: BingDateGrain): Exclude<DatePreset, 'custom'> {
  return grain === 'day' ? '7d' : 'this_month';
}

function rangeForPreset(preset: Exclude<DatePreset, 'custom'>): DateRange {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const end = daysAgo(1);

  if (preset === 'this_week' || preset === 'this_month_today') return getCurrentCalendarRange(preset === 'this_week' ? 'thisWeek' : 'thisMonth', dateAtUTC(year, month, today.getUTCDate()));
  if (preset === 'today') return { start: daysAgo(0), end: daysAgo(0) };
  if (preset === 'yesterday') {
    return { start: end, end };
  }
  if (preset === '30d') return { start: daysAgo(30), end };
  if (preset === 'week_to_yesterday') {
    const monday = new Date();
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    const start = monday.toISOString().slice(0, 10);
    return start <= end ? { start, end } : { start: daysAgo(7), end };
  }
  if (preset === 'this_month') return { start: dateAtUTC(year, month, 1), end };
  if (preset === 'last_3_months') return { start: dateAtUTC(year, month - 2, 1), end };
  if (preset === 'last_6_months') return { start: dateAtUTC(year, month - 5, 1), end };
  if (preset === 'this_quarter') return { start: dateAtUTC(year, Math.floor(month / 3) * 3, 1), end };
  if (preset === 'this_year') return { start: dateAtUTC(year, 0, 1), end };
  return { start: daysAgo(7), end };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function DateRangeControls({
  showFreshness = false,
  dateGrain,
  preset,
  range,
  onPresetChange,
  onRangeChange,
}: {
  showFreshness?: boolean;
  dateGrain: BingDateGrain;
  preset: DatePreset;
  range: DateRange;
  onPresetChange: (preset: Exclude<DatePreset, 'custom'>) => void;
  onRangeChange: (range: DateRange) => void;
}) {
  const supportsToday = useDeliverySiteScope();
  return (
    <div className="marketing-time-range-filter">
      <span className="data-filter-inline-label">日期</span>
      <div className="page-tabs marketing-time-presets" role="group" aria-label="日期范围">
        {datePresetsForGrain(dateGrain).filter(item => item.value !== 'today' || supportsToday).map(item => (
          <button type="button" key={item.value} className={`page-tab${preset === item.value ? ' active' : ''}`} onClick={() => onPresetChange(item.value)}>
            {item.label}
          </button>
        ))}
      </div>
      <MarketingDateRangePicker start={range.start} end={range.end} max={daysAgo(0)} grain={dateGrain} onChange={nextRange => onRangeChange(alignMarketingDateRange(nextRange, dateGrain))} />
      {showFreshness && <AdDataFreshnessNote />}
    </div>
  );
}

function DashboardFilterControls({
  filters,
  values,
  onChange,
  labelDataset,
}: {
  filters: DashboardFilter[];
  values: Record<string, string[]>;
  onChange: (key: string, next: string[]) => void;
  labelDataset?: string;
}) {
  if (filters.length === 0 || !labelDataset) return null;
  return <>
    {filters.map(filter => (
      <div key={filter.key} className="dimension-select-row" style={{ display: 'flex', alignItems: 'center' }}>
        <span className="dimension-select-label">{filter.label}</span>
        <ReportLabelSelect
          dataset={labelDataset}
          field={filter.key}
          pageFields={filters.map(item => item.key)}
          value={values[filter.key] ?? []}
          onChange={next => onChange(filter.key, next)}
          width={220}
        />
      </div>
    ))}
  </>;
}

export function rtbDimensionsForGrain(grain: BingDateGrain): RtbDimension[] {
  return grain === 'day' ? [DATE_DIMENSION] : [DATE_DIMENSION, COUNTRY_DIMENSION];
}

function normalizeEfficiencyDimensions(next: string[], options: RtbDimension[]): string[] {
  const allowed = new Set(options.map(item => item.key));
  const dimensions = next.filter(key => allowed.has(key));
  return dimensions.length > 0 ? [...new Set(dimensions)] : ['date'];
}

function dimensionLabel(key: string, options: RtbDimension[]): string {
  return options.find(item => item.key === key)?.label ?? key;
}

function emptyRtbMetrics(): RtbMetricAccumulator {
  return { impressions: 0, clicks: 0, addToCartCount: 0, spend: 0, purchases: 0, revenue: 0 };
}

function addRtbMetrics(target: RtbMetricAccumulator, row: AppLikeOverviewRow) {
  target.impressions += row.impressions ?? 0;
  target.clicks += row.clicks ?? 0;
  target.addToCartCount += row.add_to_cart_count ?? 0;
  target.spend += row.spend ?? 0;
  target.purchases += row.purchases ?? 0;
  target.revenue += row.revenue ?? 0;
}

function metricsToRtbRow(metrics: RtbMetricAccumulator): AppLikeOverviewRow {
  const { impressions, clicks, addToCartCount, spend, purchases, revenue } = metrics;
  return {
    date_label: '',
    impressions,
    clicks,
    add_to_cart_count: addToCartCount,
    ctr: impressions > 0 ? clicks / impressions : 0,
    cpc: clicks > 0 ? spend / clicks : 0,
    spend,
    cpm: impressions > 0 ? spend / impressions * 1000 : 0,
    purchases,
    revenue,
    cpa: purchases > 0 ? spend / purchases : 0,
    account_roi: spend > 0 ? revenue / spend : 0,
    aov: purchases > 0 ? revenue / purchases : 0,
    click_purchase_rate: clicks > 0 ? purchases / clicks : 0,
    cost_per_add_to_cart: addToCartCount > 0 ? spend / addToCartCount : 0,
    click_add_to_cart_rate: clicks > 0 ? addToCartCount / clicks : 0,
    purchase_conversion_rate: addToCartCount > 0 ? purchases / addToCartCount : 0,
  };
}

function buildRtbTree(rows: AppLikeOverviewRow[], dimensions: string[], options: RtbDimension[]): RtbTreeNode[] {
  const roots: RtbTreeNode[] = [];
  const rootMap = new Map<string, RtbTreeNode>();
  for (const row of rows) {
    let siblings = roots;
    let siblingMap = rootMap;
    let parentID = '';
    for (const [depth, dimKey] of dimensions.entries()) {
      const dimension = options.find(item => item.key === dimKey) ?? DATE_DIMENSION;
      const rawValue = row[dimension.rowKey];
      const value = String(rawValue || (dimKey === 'country' ? '未知' : '未分类'));
      const id = `${parentID}${dimKey}:${value}`;
      let node = siblingMap.get(id);
      if (!node) {
        node = {
          id,
          depth,
          dimKey,
          value,
          sortValue: value,
          metrics: emptyRtbMetrics(),
          children: [],
          childMap: new Map<string, RtbTreeNode>(),
        };
        siblingMap.set(id, node);
        siblings.push(node);
      }
      addRtbMetrics(node.metrics, row);
      parentID = `${id}/`;
      siblings = node.children;
      siblingMap = node.childMap;
    }
  }
  return roots;
}

function sortRtbTree(nodes: RtbTreeNode[], sort: RtbSortState): RtbTreeNode[] {
  const direction = sort?.direction === 'desc' ? -1 : 1;
  return [...nodes]
    .sort((left, right) => {
      const leftRow = metricsToRtbRow(left.metrics) as unknown as Record<string, SortValue>;
      const rightRow = metricsToRtbRow(right.metrics) as unknown as Record<string, SortValue>;
      const leftValue = sort?.key && sort.key !== 'dimension' ? leftRow[sort.key] : left.sortValue;
      const rightValue = sort?.key && sort.key !== 'dimension' ? rightRow[sort.key] : right.sortValue;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') return (leftValue - rightValue) * direction;
      return String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'zh-CN') * direction;
    })
    .map(node => ({ ...node, children: sortRtbTree(node.children, sort) }));
}

function visibleRtbTreeRows(nodes: RtbTreeNode[], expanded: Set<string>): RtbTreeNode[] {
  const out: RtbTreeNode[] = [];
  for (const node of nodes) {
    out.push(node);
    if (node.children.length > 0 && expanded.has(node.id)) out.push(...visibleRtbTreeRows(node.children, expanded));
  }
  return out;
}

function expandableRtbTreeNodeIDs(nodes: RtbTreeNode[]): string[] {
  return nodes.flatMap(node => [
    ...(node.children.length > 0 ? [node.id] : []),
    ...expandableRtbTreeNodeIDs(node.children),
  ]);
}

function RtbDimensionPicker({
  options,
  selected,
  onChange,
}: {
  options: RtbDimension[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="dimension-select-row" style={{ display: 'flex' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={options.map(item => ({ value: item.key, label: item.label }))}
        value={selected}
        onChange={next => onChange(normalizeEfficiencyDimensions(next, options))}
        countLabelFormatter={count => `已选 ${count} 项`}
        tagDisplay
        style={{ width: 420 }}
      />
    </div>
  );
}

function RtbTrendChart({ rows, userLevel, metrics }: { rows: AppLikeOverviewRow[]; userLevel: number; metrics: TrendMetric[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const visibleMetrics = useMemo(
    () => metrics.filter(metric => !metric.permKey || userLevel >= 2),
    [metrics, userLevel],
  );

  useEffect(() => {
    if (!ref.current || rows.length === 0 || visibleMetrics.length === 0) {
      chartRef.current?.dispose();
      chartRef.current = null;
      return;
    }
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);

    const rootStyles = getComputedStyle(document.documentElement);
    const metricByLabel = new Map(visibleMetrics.map(metric => [metric.label, metric]));
    chartRef.current.setOption({
      color: visibleMetrics.map(metric => rootStyles.getPropertyValue(metric.colorToken).trim()),
      tooltip: {
        trigger: 'axis',
        formatter: (rawParams: unknown) => {
          const params = rawParams as Array<{ axisValueLabel: string; seriesName: string; value: number; marker: string }>;
          if (!params?.length) return '';
          const lines = params.map(param => {
            const metric = metricByLabel.get(param.seriesName);
            return `${param.marker}${param.seriesName} ${metric ? metric.fmt(Number(param.value)) : param.value}`;
          });
          return `${params[0].axisValueLabel}<br/>${lines.join('<br/>')}`;
        },
      },
      legend: {
        bottom: 0,
        itemWidth: 10,
        itemHeight: 8,
        selected: Object.fromEntries(visibleMetrics.map(metric => [metric.label, metric.defaultSelected ?? false])),
        textStyle: { color: rootStyles.getPropertyValue('--text2').trim(), fontSize: 12 },
      },
      grid: { left: 58, right: 28, top: 24, bottom: 52 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: rows.map(row => row.date_label),
        axisLabel: { color: rootStyles.getPropertyValue('--text2').trim(), fontSize: 11, rotate: rows.length > 10 ? 28 : 0 },
        axisLine: { lineStyle: { color: rootStyles.getPropertyValue('--border2').trim() } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: rootStyles.getPropertyValue('--text2').trim(),
          fontSize: 11,
          formatter: (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 2 }),
        },
        splitLine: { lineStyle: { color: rootStyles.getPropertyValue('--border').trim() } },
      },
      series: visibleMetrics.map(metric => ({
        name: metric.label,
        type: 'line',
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        data: rows.map(row => Number(row[metric.key as keyof AppLikeOverviewRow] ?? 0)),
      })),
    }, true);
  }, [rows, visibleMetrics]);

  useEffect(() => {
    const resize = () => chartRef.current?.resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => () => {
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return <div ref={ref} style={{ width: '100%', height: 300 }} />;
}

export default function RtbDeliveryDashboard() {
  const brand = useAppStore(state => state.brand);
  if (brand === 'zeelool') {
    return (
      <AppLikeDeliveryDashboard
        storageKey="rtb_dashboard_hidden_cols"
        platformLabel="RTB"
        reportKey="ad_dashboard_rtb_overview"
        supportedRegions={['us']}
        getOverview={rtbDashboardApi.getOverview}
        metrics={ZEELOOL_RTB_METRICS as any}
      />
    );
  }
  return <VooglamRtbDeliveryDashboard />;
}

interface VooglamOverviewParams {
  brand_id: string;
  site_id: string;
  start_date: string;
  end_date: string;
  date_grain: BingDateGrain;
  dimensions?: string;
  [key: string]: string | undefined;
}

interface AppliedEfficiencyQuery {
  params: VooglamOverviewParams;
  dimensions: string[];
}

interface PendingUsageQuery {
  interactionID: string;
  startedAt: number;
  module: string;
  properties: Record<string, unknown>;
}

export interface DashboardFilter {
  key: string;
  label: string;
  visibleForGrain?: (grain: BingDateGrain) => boolean;
}

export interface VooglamDashboardConfig {
  platformLabel: string;
  reportKey: string;
  storageKey: string;
  getTrend: (params: VooglamOverviewParams) => Promise<{ data: { list: AppLikeOverviewRow[] } }>;
  getEfficiency: (params: VooglamOverviewParams) => Promise<{ data: { list: AppLikeOverviewRow[]; summary: AppLikeOverviewRow | null } }>;
  efficiencyMetrics: MetricField[];
  trendMetrics: TrendMetric[];
  dimensionsForGrain: (grain: BingDateGrain) => RtbDimension[];
  trendFilters?: DashboardFilter[];
  efficiencyFilters?: DashboardFilter[];
  labelDataset?: string;
  efficiencyTitle?: string;
  campaignEfficiency?: { title: string; dimensions: string[] };
  freshnessPosition?: 'after-date' | 'after-filters';
  stickyTopFilters?: boolean;
}

const VOOGLAM_RTB_CONFIG: VooglamDashboardConfig = {
  platformLabel: 'RTB',
  reportKey: 'ad_dashboard_rtb_overview',
  storageKey: 'vooglam_rtb_dashboard_hidden_cols',
  getTrend: rtbDashboardApi.getTrend,
  getEfficiency: rtbDashboardApi.getEfficiency,
  efficiencyMetrics: EFFICIENCY_METRICS,
  trendMetrics: TREND_METRICS,
  dimensionsForGrain: rtbDimensionsForGrain,
  efficiencyFilters: [{ key: 'country', label: '国家/地区', visibleForGrain: grain => grain !== 'day' }],
  labelDataset: 'vooglam_rtb_delivery',
};

function VooglamRtbDeliveryDashboard() {
  return <VooglamPerformanceDashboard config={VOOGLAM_RTB_CONFIG} />;
}

export function VooglamPerformanceDashboard({ config }: { config: VooglamDashboardConfig }) {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);

  const [trendPreset, setTrendPreset] = useState<DatePreset>('7d');
  const [trendRange, setTrendRange] = useState<DateRange>(() => rangeForPreset('7d'));
  const [trendRows, setTrendRows] = useState<AppLikeOverviewRow[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState('');
  const [trendFilterValues, setTrendFilterValues] = useState<Record<string, string[]>>({});
  const [queryVersion, setQueryVersion] = useState(0);
  const pendingQueriesRef = useRef(new Map<'rtb_trend' | 'rtb_efficiency', PendingUsageQuery>());

  const [efficiencyGrain, setEfficiencyGrain] = useState<BingDateGrain>('day');
  const [efficiencyPreset, setEfficiencyPreset] = useState<DatePreset>('7d');
  const [efficiencyRange, setEfficiencyRange] = useState<DateRange>(() => rangeForPreset('7d'));
  const [efficiencyDimensions, setEfficiencyDimensions] = useState<string[]>(['date']);
  const [efficiencyRows, setEfficiencyRows] = useState<AppLikeOverviewRow[]>([]);
  const [efficiencySummary, setEfficiencySummary] = useState<AppLikeOverviewRow | null>(null);
  const [efficiencyLoading, setEfficiencyLoading] = useState(false);
  const [efficiencyError, setEfficiencyError] = useState('');
  const [efficiencyFilterValues, setEfficiencyFilterValues] = useState<Record<string, string[]>>({});
  const [appliedEfficiencyQuery, setAppliedEfficiencyQuery] = useState<AppliedEfficiencyQuery | null>(null);
  const [campaignRows, setCampaignRows] = useState<AppLikeOverviewRow[]>([]);
  const [campaignSummary, setCampaignSummary] = useState<AppLikeOverviewRow | null>(null);
  const [campaignDimensions, setCampaignDimensions] = useState<string[]>(() => config.campaignEfficiency?.dimensions ?? []);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(config.storageKey);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const currentSite = useMemo(() => {
    if (brand !== 'vooglam') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site || regionFromSiteCode(site.code) !== 'us') return null;
    return { id: site.id, code: site.code };
  }, [brand, siteId, sites]);
  const efficiencySection = useLazySection(Boolean(currentSite));

  const visibleMetrics = useMemo(
    () => config.efficiencyMetrics.filter(metric => !hiddenMetricKeys.has(metric.key)),
    [config.efficiencyMetrics, hiddenMetricKeys],
  );
  const [sort, setSort] = useState<RtbSortState>(null);
  const efficiencyDimensionOptions = useMemo(
    () => config.dimensionsForGrain(efficiencyGrain),
    [config, efficiencyGrain],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const treeRoots = useMemo(
    () => buildRtbTree(efficiencyRows, efficiencyDimensions, efficiencyDimensionOptions),
    [efficiencyDimensionOptions, efficiencyDimensions, efficiencyRows],
  );
  const sortedTreeRoots = useMemo(() => sortRtbTree(treeRoots, sort), [sort, treeRoots]);
  const visibleTreeRows = useMemo(() => visibleRtbTreeRows(sortedTreeRoots, expanded), [expanded, sortedTreeRoots]);
  const expandableNodeIDs = useMemo(() => expandableRtbTreeNodeIDs(treeRoots), [treeRoots]);
  const showDynamicDimensions = efficiencyDimensionOptions.length > 1;
  const [campaignExpanded, setCampaignExpanded] = useState<Set<string>>(new Set());
  const campaignTreeRoots = useMemo(
    () => buildRtbTree(campaignRows, campaignDimensions, efficiencyDimensionOptions),
    [campaignDimensions, campaignRows, efficiencyDimensionOptions],
  );
  const sortedCampaignTreeRoots = useMemo(
    () => sortRtbTree(campaignTreeRoots, sort),
    [campaignTreeRoots, sort],
  );
  const visibleCampaignRows = useMemo(
    () => visibleRtbTreeRows(sortedCampaignTreeRoots, campaignExpanded),
    [campaignExpanded, sortedCampaignTreeRoots],
  );
  const campaignExpandableNodeIDs = useMemo(
    () => expandableRtbTreeNodeIDs(campaignTreeRoots),
    [campaignTreeRoots],
  );
  const trendFilters = useMemo(
    () => (config.trendFilters ?? []).filter(filter => !filter.visibleForGrain || filter.visibleForGrain('day')),
    [config.trendFilters],
  );
  const efficiencyFilters = useMemo(
    () => (config.efficiencyFilters ?? []).filter(filter => !filter.visibleForGrain || filter.visibleForGrain(efficiencyGrain)),
    [config.efficiencyFilters, efficiencyGrain],
  );

  const completeTrackedQuery = useCallback((featureKey: 'rtb_trend' | 'rtb_efficiency', outcome: 'success' | 'failed') => {
    const pending = pendingQueriesRef.current.get(featureKey);
    if (!pending) return;
    pendingQueriesRef.current.delete(featureKey);
    trackQueryEvent('completed', {
      interaction_id: pending.interactionID,
      page_key: 'ad-analysis_ad-dashboard_rtb',
      feature_key: featureKey,
      action: 'query',
      outcome,
      duration_ms: Math.max(0, Math.round(performance.now() - pending.startedAt)),
      properties: { module: pending.module, ...pending.properties },
    });
  }, []);

  const startTrackedQuery = useCallback((featureKey: 'rtb_trend' | 'rtb_efficiency', module: string) => {
    let interactionID: string;
    try { interactionID = crypto.randomUUID(); } catch { interactionID = `query-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
    const properties = {
      trend_date_range: `${trendRange.start}~${trendRange.end}`,
      efficiency_date_range: `${efficiencyRange.start}~${efficiencyRange.end}`,
      efficiency_grain: efficiencyGrain,
      trend_filters: trendFilterValues,
      efficiency_filters: efficiencyFilterValues,
    };
    const pending = { interactionID, startedAt: performance.now(), module, properties };
    pendingQueriesRef.current.set(featureKey, pending);
    trackQueryEvent('submitted', {
      interaction_id: interactionID,
      page_key: 'ad-analysis_ad-dashboard_rtb',
      feature_key: featureKey,
      action: 'query',
      properties: { module, ...properties },
    });
  }, [efficiencyFilterValues, efficiencyGrain, efficiencyRange.end, efficiencyRange.start, trendFilterValues, trendRange.end, trendRange.start]);
  const setTrendFilter = useCallback((key: string, next: string[]) => {
    trackFeatureAction({
      page_key: 'ad-analysis_ad-dashboard_rtb',
      feature_key: 'rtb_trend',
      action: 'filter_select',
      properties: { filter_key: key, selected_values: next },
    });
    setTrendFilterValues(current => ({ ...current, [key]: next }));
  }, []);
  const setEfficiencyFilter = useCallback((key: string, next: string[]) => {
    trackFeatureAction({
      page_key: 'ad-analysis_ad-dashboard_rtb',
      feature_key: 'rtb_efficiency',
      action: 'filter_select',
      properties: { filter_key: key, selected_values: next },
    });
    setEfficiencyFilterValues(current => ({ ...current, [key]: next }));
  }, []);

  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  const toggleNode = useCallback((id: string) => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setExpanded(new Set(expandableNodeIDs)), [expandableNodeIDs]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);
  const toggleCampaignNode = useCallback((id: string) => {
    setCampaignExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const expandAllCampaigns = useCallback(() => setCampaignExpanded(new Set(campaignExpandableNodeIDs)), [campaignExpandableNodeIDs]);
  const collapseAllCampaigns = useCallback(() => setCampaignExpanded(new Set()), []);

  const fetchTrend = useCallback(async () => {
    if (!brandId || !currentSite) return;
    setTrendLoading(true);
    setTrendError('');
    try {
      const result = await config.getTrend({
        brand_id: brandId,
        site_id: currentSite.id,
        start_date: trendRange.start,
        end_date: trendRange.end,
        date_grain: 'day',
        ...Object.fromEntries(Object.entries(trendFilterValues).filter(([, values]) => values.length > 0).map(([key, values]) => [key, values.join(',')])),
      });
      setTrendRows(result.data?.list ?? []);
      completeTrackedQuery('rtb_trend', 'success');
    } catch (error) {
      setTrendRows([]);
      setTrendError(error instanceof Error ? error.message : '加载核心指标走势失败');
      completeTrackedQuery('rtb_trend', 'failed');
    } finally {
      setTrendLoading(false);
    }
  }, [brandId, completeTrackedQuery, config, currentSite, trendFilterValues, trendRange.end, trendRange.start]);

  const fetchEfficiency = useCallback(async () => {
    if (!brandId || !currentSite) return;
    setEfficiencyLoading(true);
    setEfficiencyError('');
    try {
      const params = {
        brand_id: brandId,
        site_id: currentSite.id,
        start_date: efficiencyRange.start,
        end_date: efficiencyRange.end,
        date_grain: efficiencyGrain,
        dimensions: efficiencyDimensions.join(','),
        ...Object.fromEntries(Object.entries(efficiencyFilterValues).filter(([key, values]) => values.length > 0 && efficiencyFilters.some(filter => filter.key === key)).map(([key, values]) => [key, values.join(',')])),
      };
      const [result, campaignResult] = await Promise.all([
        config.getEfficiency(params),
        config.campaignEfficiency ? config.getEfficiency({ ...params, dimensions: campaignDimensions.join(',') }) : Promise.resolve(null),
      ]);
      setEfficiencyRows(result.data?.list ?? []);
      setEfficiencySummary(result.data?.summary ?? null);
      setCampaignRows(campaignResult?.data?.list ?? []);
      setCampaignSummary(campaignResult?.data?.summary ?? null);
      setAppliedEfficiencyQuery({ params: { ...params }, dimensions: [...efficiencyDimensions] });
      setExpanded(new Set());
      setCampaignExpanded(new Set());
      completeTrackedQuery('rtb_efficiency', 'success');
    } catch (error) {
      setEfficiencyRows([]);
      setEfficiencySummary(null);
      setAppliedEfficiencyQuery(null);
      setCampaignRows([]);
      setCampaignSummary(null);
      setEfficiencyError(error instanceof Error ? error.message : '加载投入效率失败');
      completeTrackedQuery('rtb_efficiency', 'failed');
    } finally {
      setEfficiencyLoading(false);
    }
  }, [brandId, campaignDimensions, completeTrackedQuery, config, currentSite, efficiencyDimensions, efficiencyFilterValues, efficiencyFilters, efficiencyGrain, efficiencyRange.end, efficiencyRange.start]);

  useEffect(() => {
    if (currentSite) fetchTrend();
    else if (sitesLoaded) {
      setTrendRows([]);
      setTrendError('');
    }
  }, [currentSite, queryVersion, sitesLoaded]);

  useEffect(() => {
    if (currentSite) {
      if (efficiencySection.ready) void fetchEfficiency();
      return;
    }
    if (sitesLoaded) {
      setEfficiencyRows([]);
      setEfficiencySummary(null);
      setCampaignRows([]);
      setCampaignSummary(null);
      setEfficiencyError('');
    }
  }, [currentSite, efficiencySection.ready, queryVersion, sitesLoaded]);

  const query = (featureKey: 'rtb_trend' | 'rtb_efficiency', module: string) => {
    startTrackedQuery(featureKey, module);
    setQueryVersion(version => version + 1);
  };
  useMarketingViewSection('page', {
    label: '趋势与投入效率', state: { trendPreset, trendRange, trendFilterValues, efficiencyGrain, efficiencyPreset, efficiencyRange, efficiencyDimensions, efficiencyFilterValues, campaignDimensions, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setTrendPreset(value.trendPreset); setTrendRange(value.trendRange); setTrendFilterValues(value.trendFilterValues); setEfficiencyGrain(value.efficiencyGrain); setEfficiencyPreset(value.efficiencyPreset); setEfficiencyRange(value.efficiencyRange); setEfficiencyDimensions(value.efficiencyDimensions); setEfficiencyFilterValues(value.efficiencyFilterValues); setCampaignDimensions(value.campaignDimensions); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: trendLoading || efficiencyLoading, query: () => { setQueryVersion(version => version + 1); },
    resolve: value => { return { ...value, trendRange: value.trendPreset === 'custom' ? value.trendRange : rangeForPreset(value.trendPreset), efficiencyRange: value.efficiencyPreset === 'custom' ? value.efficiencyRange : alignMarketingDateRange(rangeForPreset(value.efficiencyPreset), value.efficiencyGrain) }; },
  });

  const reset = (featureKey: 'rtb_trend' | 'rtb_efficiency', module: string) => {
    trackFeatureAction({
      page_key: 'ad-analysis_ad-dashboard_rtb',
      feature_key: featureKey,
      action: 'reset',
      properties: { module },
    });
    setTrendPreset('7d');
    setTrendRange(rangeForPreset('7d'));
    setTrendFilterValues({});
    setEfficiencyGrain('day');
    setEfficiencyPreset('7d');
    setEfficiencyRange(rangeForPreset('7d'));
    setEfficiencyDimensions(['date']);
    setEfficiencyFilterValues({});
    setCampaignDimensions(config.campaignEfficiency?.dimensions ?? []);
    setQueryVersion(version => version + 1);
  };

  useEffect(() => {
    if (sort && sort.key !== 'dimension' && hiddenMetricKeys.has(sort.key)) setSort(null);
  }, [hiddenMetricKeys, setSort, sort]);

  const changeTrendPreset = useCallback((preset: Exclude<DatePreset, 'custom'>) => {
    setTrendPreset(preset);
    setTrendRange(rangeForPreset(preset));
  }, []);
  const changeEfficiencyPreset = useCallback((preset: Exclude<DatePreset, 'custom'>) => {
    setEfficiencyPreset(preset);
    setEfficiencyRange(alignMarketingDateRange(rangeForPreset(preset), efficiencyGrain));
  }, [efficiencyGrain]);
  const changeTrendRange = useCallback((range: DateRange) => {
    setTrendPreset('custom');
    setTrendRange(range);
  }, []);
  const changeEfficiencyRange = useCallback((range: DateRange) => {
    setEfficiencyPreset('custom');
    setEfficiencyRange(alignMarketingDateRange(range, efficiencyGrain));
  }, [efficiencyGrain]);

  const toggleMetricHidden = useCallback((key: string) => {
    setHiddenMetricKeys(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(config.storageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, [config.storageKey]);
  const resetMetricHidden = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(config.storageKey); } catch {}
  }, [config.storageKey]);

  const renderMetric = useCallback((row: AppLikeOverviewRow, metric: MetricField) => {
    const value = metric.fmt(Number(row[metric.key as keyof AppLikeOverviewRow] ?? 0));
    return metric.permKey ? sf(value, metric.permKey, userLevel).value ?? '锁定' : value;
  }, [userLevel]);

  const handleExport = useCallback(async () => {
    if (!appliedEfficiencyQuery) return;
    const appliedDimensionOptions = config.dimensionsForGrain(appliedEfficiencyQuery.params.date_grain);
    try {
      await enqueueReportExport({
        report_key: config.reportKey,
        title: `Vooglam ${config.platformLabel}投入效率`,
        file_name: `Vooglam_${config.platformLabel}投入效率_${appliedEfficiencyQuery.params.start_date}_${appliedEfficiencyQuery.params.end_date}.csv`,
        brand_id: appliedEfficiencyQuery.params.brand_id,
        site_id: appliedEfficiencyQuery.params.site_id,
        params: {
          ...appliedEfficiencyQuery.params,
        },
        columns: [
          ...appliedEfficiencyQuery.dimensions.map(dimension => ({
            key: appliedDimensionOptions.find(item => item.key === dimension)?.rowKey ?? 'date_label',
            label: dimensionLabel(dimension, appliedDimensionOptions),
          })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '导出任务提交失败', 'error');
    }
  }, [appliedEfficiencyQuery, config, enqueueReportExport, showToast, visibleMetrics]);

  return (
    <div className="page-section visible">
      <div
        className="dashboard-sticky-filters"
        style={config.stickyTopFilters === false ? { position: 'static', margin: '0 0 12px', padding: 0, boxShadow: 'none' } : undefined}
      >
        <div className="data-filter-card">
          <div className="data-filter-row time">
            <DateRangeControls showFreshness={config.freshnessPosition !== 'after-filters'} dateGrain="day" preset={trendPreset} range={trendRange} onPresetChange={changeTrendPreset} onRangeChange={changeTrendRange} />
            <DashboardFilterControls filters={trendFilters} values={trendFilterValues} onChange={setTrendFilter} labelDataset={config.labelDataset} />
            {config.freshnessPosition === 'after-filters' && <AdDataFreshnessNote />}
            <DashboardQueryActions loading={trendLoading || efficiencyLoading} onReset={() => reset('rtb_trend', '核心指标走势')} onQuery={() => query('rtb_trend', '核心指标走势')} tracking={false} />
          </div>
        </div>
      </div>

      {trendError && <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius-xs)', background: 'var(--red-bg)', color: 'var(--red)', fontSize: 13 }}>{trendError}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden', position: 'relative' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>核心指标走势</div>
        {trendLoading && trendRows.length === 0 ? <TableLoadingState />
          : !currentSite ? <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>请在左侧切换到 Vooglam US 站点查看 {config.platformLabel} 核心指标走势。</div>
            : trendRows.length === 0 ? <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>当前日期范围没有 {config.platformLabel} 趋势数据，请调整日期后重试。</div>
              : <RtbTrendChart rows={trendRows} userLevel={userLevel} metrics={config.trendMetrics} />}
        {trendLoading && trendRows.length > 0 && <TableLoadingOverlay />}
      </div>

      <div ref={efficiencySection.ref} className="data-filter-card" style={{ marginTop: 16 }}>
        <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期粒度</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {DATE_GRAINS.map(item => (
              <div
                key={item.value}
                className={`page-tab${efficiencyGrain === item.value ? ' active' : ''}`}
                onClick={() => {
                  const changingDatePresetGroup = (efficiencyGrain === 'day') !== (item.value === 'day');
                  setEfficiencyGrain(item.value);
                  setEfficiencyDimensions(current => normalizeEfficiencyDimensions(current, config.dimensionsForGrain(item.value)));
                  if (config.campaignEfficiency) {
                    setCampaignDimensions(current => normalizeEfficiencyDimensions(current, config.dimensionsForGrain(item.value)));
                  }
                  if (changingDatePresetGroup) {
                    const preset = defaultDatePresetForGrain(item.value);
                    setEfficiencyPreset(preset);
                    setEfficiencyRange(alignMarketingDateRange(rangeForPreset(preset), item.value));
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
          <DateRangeControls dateGrain={efficiencyGrain} preset={efficiencyPreset} range={efficiencyRange} onPresetChange={changeEfficiencyPreset} onRangeChange={changeEfficiencyRange} />
          <DashboardFilterControls filters={efficiencyFilters} values={efficiencyFilterValues} onChange={setEfficiencyFilter} labelDataset={config.labelDataset} />
          <DashboardQueryActions loading={trendLoading || efficiencyLoading} onReset={() => reset('rtb_efficiency', '投入效率')} onQuery={() => query('rtb_efficiency', '投入效率')} tracking={false} />
        </div>
      </div>

      {efficiencyError && <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius-xs)', background: 'var(--red-bg)', color: 'var(--red)', fontSize: 13 }}>{efficiencyError}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{config.efficiencyTitle ?? '投入效率'}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {showDynamicDimensions && <>
              <button type="button" className="btn btn-outline btn-sm" onClick={expandAll} disabled={efficiencyLoading || expandableNodeIDs.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Maximize2 size={14} strokeWidth={2} />
                全部展开
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={collapseAll} disabled={efficiencyLoading || expanded.size === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Minimize2 size={14} strokeWidth={2} />
                全部收起
              </button>
            </>}
            <button type="button" className="btn btn-outline btn-sm" onClick={handleExport} disabled={efficiencyLoading || !currentSite} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Download size={14} strokeWidth={2} />
              导出
            </button>
            <ColumnToggle
              cols={config.efficiencyMetrics.map(metric => ({ key: metric.key, label: metric.label }))}
              hiddenKeys={hiddenMetricKeys}
              onToggle={toggleMetricHidden}
              onReset={resetMetricHidden}
            />
          </div>
        </div>
        {showDynamicDimensions && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)' }}>
            <RtbDimensionPicker options={efficiencyDimensionOptions} selected={efficiencyDimensions} onChange={next => {
              trackFeatureAction({
                page_key: 'ad-analysis_ad-dashboard_rtb',
                feature_key: 'rtb_efficiency',
                action: 'dimension_change',
                properties: { dimensions: next },
              });
              setEfficiencyDimensions(next);
            }} />
          </div>
        )}
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 'calc(100vh - 235px)', overflow: 'auto' }}>
          <table style={{ minWidth: 1300 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: '1%', minWidth: 160, maxWidth: 360 }}>
                  <DimensionHeaderTooltip labels={efficiencyDimensionOptions.map(dimension => dimension.label)} />
                </th>
                {visibleMetrics.map(metric => (
                  <SortableHeader key={metric.key} sortKey={metric.key} sort={sort} onSort={toggleSort} align="right" disabled={Boolean(metric.permKey && userLevel < 2)} style={{ whiteSpace: 'nowrap' }}>
                    <MetricDescriptionTooltip label={metric.label} />
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {efficiencySummary && (
                <tr className="report-summary-row" style={{ fontWeight: 700, background: 'var(--surface)' }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', color: 'var(--text2)', whiteSpace: 'nowrap' }}>总计</td>
                  {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(efficiencySummary, metric)}</td>)}
                </tr>
              )}
              {efficiencyLoading && efficiencyRows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1}><TableLoadingCell /></td></tr>
              ) : !currentSite ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>请在左侧切换到 Vooglam US 站点查看 {config.platformLabel} 投入效率。</td></tr>
              ) : efficiencyRows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>当前筛选范围没有 {config.platformLabel} 投入效率数据，请调整日期后重试。</td></tr>
              ) : visibleTreeRows.map((node, index) => {
                const row = metricsToRtbRow(node.metrics);
                return (
                  <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', width: '1%', minWidth: 160, maxWidth: 360 }}>
                      <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                        {node.children.length > 0 && (
                          <button type="button" className="tree-toggle-btn" onClick={() => toggleNode(node.id)} aria-label={expanded.has(node.id) ? '收起' : '展开'} aria-expanded={expanded.has(node.id)}>
                            {expanded.has(node.id) ? '−' : '+'}
                          </button>
                        )}
                        {node.children.length === 0 && node.depth > 0 && <span aria-hidden="true" style={{ width: 16, flexShrink: 0 }} />}
                        {(node.children.length > 0 || node.depth > 0) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                            {dimensionLabel(node.dimKey, efficiencyDimensionOptions)}
                          </span>
                        )}
                        <TruncatedValueTooltip value={node.value} />
                      </span>
                    </td>
                    {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(row, metric)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {efficiencyLoading && efficiencyRows.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>

      {config.campaignEfficiency && (
        <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {config.campaignEfficiency.title}
            </span>
            {showDynamicDimensions && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={expandAllCampaigns} disabled={efficiencyLoading || campaignExpandableNodeIDs.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Maximize2 size={14} strokeWidth={2} />
                全部展开
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={collapseAllCampaigns} disabled={efficiencyLoading || campaignExpanded.size === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Minimize2 size={14} strokeWidth={2} />
                全部收起
              </button>
            </div>}
          </div>
          {showDynamicDimensions && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)' }}>
              <RtbDimensionPicker options={efficiencyDimensionOptions} selected={campaignDimensions} onChange={setCampaignDimensions} />
            </div>
          )}
          <div className="table-wrap" style={{ position: 'relative', maxHeight: 'calc(100vh - 235px)', overflow: 'auto' }}>
            <table style={{ minWidth: 1300 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                <tr>
                  <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: '1%', minWidth: 160, maxWidth: 360 }}>
                    <DimensionHeaderTooltip labels={efficiencyDimensionOptions.map(dimension => dimension.label)} />
                  </th>
                  {visibleMetrics.map(metric => <SortableHeader key={metric.key} sortKey={metric.key} sort={sort} onSort={toggleSort} align="right" disabled={Boolean(metric.permKey && userLevel < 2)} style={{ whiteSpace: 'nowrap' }}><MetricDescriptionTooltip label={metric.label} /></SortableHeader>)}
                </tr>
              </thead>
              <tbody>
                {campaignSummary && (
                  <tr className="report-summary-row" style={{ fontWeight: 700, background: 'var(--surface)' }}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', color: 'var(--text2)' }}>总计</td>
                    {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(campaignSummary, metric)}</td>)}
                  </tr>
                )}
                {efficiencyLoading && campaignRows.length === 0 ? (
                  <tr><td colSpan={visibleMetrics.length + 1}><TableLoadingCell /></td></tr>
                ) : campaignRows.length === 0 ? (
                  <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>当前筛选范围没有广告系列维度数据，请调整日期或筛选条件后重试。</td></tr>
                ) : visibleCampaignRows.map((node, index) => {
                  const row = metricsToRtbRow(node.metrics);
                  return <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', width: '1%', minWidth: 160, maxWidth: 360 }}>
                      <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                        {node.children.length > 0 && <button type="button" className="tree-toggle-btn" onClick={() => toggleCampaignNode(node.id)} aria-label={campaignExpanded.has(node.id) ? '收起' : '展开'} aria-expanded={campaignExpanded.has(node.id)}>{campaignExpanded.has(node.id) ? '−' : '+'}</button>}
                        {node.children.length === 0 && node.depth > 0 && <span aria-hidden="true" style={{ width: 16, flexShrink: 0 }} />}
                        {(node.children.length > 0 || node.depth > 0) && <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{dimensionLabel(node.dimKey, efficiencyDimensionOptions)}</span>}
                        <TruncatedValueTooltip value={node.value} />
                      </span>
                    </td>
                    {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(row, metric)}</td>)}
                  </tr>;
                })}
              </tbody>
            </table>
            {efficiencyLoading && campaignRows.length > 0 && <TableLoadingOverlay />}
          </div>
        </div>
      )}
    </div>
  );
}
